import React, { useMemo, useState } from 'react';
import { PaymentMethod } from '../types/index';
import { movimentoTemPendente, dividaPendenteMovimento, pagamentosDoMovimento } from '../shared/logica/pagamentos';
import { formatDDMMYYYY } from '../utils/dateI18n';
import { ArrowLeft, ReceiptText, DollarSign, CreditCard, Clock, LogOut } from 'lucide-react';
import ModalLiquidateDebt from './ModalLiquidateDebt';

export default function SimpleDebtsView({ movements, onBack, onLogout, onUpdateMovement }) {
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [showLiquidationModal, setShowLiquidationModal] = useState(false);
  const debts = useMemo(() => {
    return movements.filter(m => movimentoTemPendente(m)).map(m => {
      const pagamentos = pagamentosDoMovimento(m);
      const debtAmount = dividaPendenteMovimento(m);
      const receivedAmount = pagamentos
        .filter(p => p.method !== PaymentMethod.PENDING)
        .reduce((acc, p) => acc + p.value, 0);

      return {
        ...m,
        debtAmount,
        receivedAmount,
        paymentMethod: PaymentMethod.PENDING
      };
    }).sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });
  }, [movements]);

  const totalDebt = debts.reduce((acc, d) => acc + d.debtAmount, 0);

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case PaymentMethod.CASH:
        return <DollarSign size={16} className="text-emerald-600" />;
      case PaymentMethod.CARD:
        return <CreditCard size={16} className="text-blue-600" />;
      case PaymentMethod.PENDING:
        return <Clock size={16} className="text-amber-600" />;
      default:
        return <DollarSign size={16} className="text-slate-400" />;
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case PaymentMethod.CASH:
        return 'Dinheiro';
      case PaymentMethod.CARD:
        return 'Multibanco';
      case PaymentMethod.TRANSFER:
        return 'Transferência';
      case PaymentMethod.CHEQUE:
        return 'Cheque';
      case PaymentMethod.PENDING:
        return 'Pendente';
      case PaymentMethod.MBWAY:
        return 'MBWay';
      default:
        return method;
    }
  };

  const handleDebtClick = (debt) => {
    setSelectedDebt(debt);
    setShowLiquidationModal(true);
  };

  const handleLiquidate = async (liquidationData) => {
    if (!selectedDebt || !onUpdateMovement) return;

    try {
      const { type, amount, paymentMethod } = liquidationData;
      const movement = selectedDebt;
      const debtAmount = movement.debtAmount;

      let updatedMovement = { ...movement };

      if (type === 'FULL') {
        // Liquidação completa - alterar PENDING para o método de pagamento
        if (movement.isMixedPayment) {
          if (movement.paymentMethod === PaymentMethod.PENDING) {
            // Primeiro método era PENDING, alterar para o método de pagamento
            updatedMovement.paymentMethod = paymentMethod;
            updatedMovement.value1 = debtAmount;
          } else if (movement.paymentMethod2 === PaymentMethod.PENDING) {
            // Segundo método era PENDING, alterar para o método de pagamento
            updatedMovement.paymentMethod2 = paymentMethod;
            updatedMovement.value2 = debtAmount;
          }
        } else {
          // Pagamento simples - alterar de PENDING para método de pagamento
          updatedMovement.paymentMethod = paymentMethod;
          updatedMovement.value = debtAmount;
        }
      } else {
        // Partial liquidation - convert to mixed payment if not already
        const remainingAmount = debtAmount - amount;

        if (!movement.isMixedPayment) {
          // Pagamento simples - converter para misto: liquidado (MULTIBANCO/TRANSFERÊNCIA) + restante (PENDING)
          updatedMovement.isMixedPayment = true;
          updatedMovement.paymentMethod = paymentMethod;
          updatedMovement.value1 = amount;
          updatedMovement.paymentMethod2 = PaymentMethod.PENDING;
          updatedMovement.value2 = remainingAmount;
          delete updatedMovement.value;
        } else {
          // Já é pagamento misto - tratar parte PENDING
          if (movement.paymentMethod === PaymentMethod.PENDING) {
            // Primeiro método é PENDING
            const existingValue1 = Number(movement.value1 || 0);
            if (existingValue1 === debtAmount) {
              // Totalmente PENDING, dividir em liquidado + restante PENDING
              updatedMovement.paymentMethod = paymentMethod;
              updatedMovement.value1 = amount;
              updatedMovement.paymentMethod2 = remainingAmount > 0 ? PaymentMethod.PENDING : movement.paymentMethod2;
              updatedMovement.value2 = remainingAmount > 0 ? remainingAmount : (movement.value2 || 0);
            } else {
              // PENDING parcial - não deveria acontecer em dívidas, mas tratar
              if (remainingAmount > 0) {
                updatedMovement.paymentMethod = paymentMethod;
                updatedMovement.value1 = amount;
                updatedMovement.paymentMethod2 = PaymentMethod.PENDING;
                updatedMovement.value2 = remainingAmount;
              } else {
                updatedMovement.paymentMethod = paymentMethod;
                updatedMovement.value1 = amount;
              }
            }
          } else if (movement.paymentMethod2 === PaymentMethod.PENDING) {
            // Segundo método é PENDING
            const existingValue2 = Number(movement.value2 || 0);
            const existingValue1 = Number(movement.value1 || 0);
            
            if (existingValue2 === debtAmount) {
              // Totalmente PENDING no segundo método
              if (remainingAmount > 0) {
                // Precisa reestruturar: manter primeiro método de pagamento, adicionar liquidado + restante PENDING
                // Since we can only have 2 methods, we need to combine first method with liquidated if same type
                // Otherwise, swap to make liquidated first and combine first with remaining
                if (movement.paymentMethod === paymentMethod) {
                  // Same payment method as first - combine first method with liquidated
                  updatedMovement.value1 = existingValue1 + amount;
                  updatedMovement.value2 = remainingAmount;
                  updatedMovement.paymentMethod2 = PaymentMethod.PENDING;
                } else {
                  // Método de pagamento diferente - trocar: liquidado primeiro, primeiro original + restante PENDING segundo
                  updatedMovement.paymentMethod = paymentMethod;
                  updatedMovement.value1 = amount;
                  updatedMovement.paymentMethod2 = PaymentMethod.PENDING;
                  updatedMovement.value2 = remainingAmount + existingValue1;
                }
              } else {
                // Fully liquidated - change second method to payment method
                if (movement.paymentMethod === paymentMethod) {
                  // Same payment method - combine with first and convert to simple payment if possible
                  updatedMovement.value1 = existingValue1 + amount;
                  updatedMovement.value2 = 0;
                  // If value2 is 0, we could convert to simple payment, but keep as mixed for consistency
                  // Just ensure paymentMethod2 is not PENDING
                  updatedMovement.paymentMethod2 = movement.paymentMethod2 !== PaymentMethod.PENDING ? movement.paymentMethod2 : paymentMethod;
                } else {
                  // Different payment method - change second to payment method
                  updatedMovement.paymentMethod2 = paymentMethod;
                  updatedMovement.value2 = amount;
                }
              }
            } else {
              // PENDING parcial - não deveria acontecer em dívidas, mas tratar
              // Combine with existing values
              if (movement.paymentMethod === paymentMethod) {
                // Same payment method - combine
                updatedMovement.value1 = existingValue1 + amount;
                updatedMovement.value2 = remainingAmount;
                updatedMovement.paymentMethod2 = remainingAmount > 0 ? PaymentMethod.PENDING : paymentMethod;
              } else {
                // Different payment method - need to restructure
                if (remainingAmount > 0) {
                  updatedMovement.paymentMethod = paymentMethod;
                  updatedMovement.value1 = amount;
                  updatedMovement.paymentMethod2 = PaymentMethod.PENDING;
                  updatedMovement.value2 = remainingAmount + existingValue1;
                } else {
                  // Fully liquidated - combine if same, otherwise change
                  if (movement.paymentMethod === paymentMethod) {
                    updatedMovement.value1 = existingValue1 + amount;
                  } else {
                    updatedMovement.paymentMethod2 = paymentMethod;
                    updatedMovement.value2 = amount;
                  }
                }
              }
            }
          }
        }
      }

      // Remover campos computados antes de atualizar
      const { debtAmount: _, receivedAmount: __, ...movementToUpdate } = updatedMovement;
      await onUpdateMovement(movementToUpdate);
      
      // Close modal only after successful update
      setShowLiquidationModal(false);
      setSelectedDebt(null);
    } catch (error) {
      console.error('Erro ao liquidar dívida:', error);
      alert('Erro ao liquidar dívida. Por favor, tente novamente.');
      // Não fechar modal em caso de erro para o utilizador poder tentar novamente
    }
  };

  return (
    <div className="min-h-screen bg-[#f1f8e9] p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white rounded-xl transition-colors bg-white shadow-sm"
            >
              <ArrowLeft size={24} className="text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Dívidas</h1>
              <p className="text-sm text-slate-500 mt-1">
                Total pendente: <span className="font-bold text-red-600">{totalDebt.toFixed(2)}€</span>
              </p>
            </div>
          </div>
          {onLogout && (
            <button
              onClick={onLogout}
              className="p-3 bg-white rounded-xl shadow-sm border border-slate-200 hover:bg-slate-50 transition-colors"
              title="Sair"
            >
              <LogOut size={20} className="text-slate-600" />
            </button>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {debts.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {debts.map((debt) => (
                <div 
                  key={debt.id} 
                  className="p-6 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => handleDebtClick(debt)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                          {getPaymentMethodIcon(debt.paymentMethod)}
                        </div>
                        <div>
                          <p className="font-bold text-slate-900">{debt.client || 'Consumidor Final'}</p>
                          <p className="text-sm text-slate-500">
                            {formatDDMMYYYY(new Date(debt.date))}
                          </p>
                        </div>
                      </div>
                      <div className="ml-13 space-y-1">
                        <p className="text-xs text-slate-400">Método: {getPaymentMethodLabel(debt.paymentMethod)}</p>
                        {debt.receivedAmount > 0 && (
                          <p className="text-xs text-slate-500">Recebido: {debt.receivedAmount.toFixed(2)}€</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-black text-red-600">{debt.debtAmount.toFixed(2)}€</p>
                      <p className="text-xs text-slate-400 mt-1">Pendente</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <ReceiptText size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-lg font-bold text-slate-600 mb-2">Sem dívidas pendentes</p>
              <p className="text-sm text-slate-400">Todas as dívidas foram recebidas</p>
            </div>
          )}
        </div>
      </div>

      {/* Liquidation Modal */}
      <ModalLiquidateDebt
        isOpen={showLiquidationModal}
        onClose={() => {
          setShowLiquidationModal(false);
          setSelectedDebt(null);
        }}
        debt={selectedDebt}
        onLiquidate={handleLiquidate}
      />
    </div>
  );
}

